import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box, Button,
  CircularProgress,
  MenuItem,
  TextField,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "commons/store/hooks";
import {
  exportFile,
  fetchStats,
  fetchStatsSurveys,
  selectStats,
  selectStatsSurveys,
  selectSurveyResourceLoading
} from "features/dashboard/redux/dashboardSlice";
import {
  StyledGlobalContainer,
  StyledGlobalStatsContainer,
  StyledHeaderContainer,
  StyledHeaderDate,
  StyledHeaderSubtitle,
  StyledHeaderTitle,
  StyledStatsDotPurple,
  StyledStatsTab,
  StyledStatsTitle,
  StyledStatsTitleText,
  StyledSText1,
  StyledSText2,
  StyledGlobalAccordionContainer,
  StyledStatsDotYellow,
  StyledGlobalAccordionFiltersContainer,
  StyledGlobalAccordionFiltersButton,
  StyledGlobalAccordionFiltersInput,
  StyledStatsDotGreen
} from "./DashboardStats.styled";
import { SurveyItem, MedicalCenterItem } from "./components";
import { FluffyItem, PurpleItem } from "./StatsTypes";
import { DATE_FORMAT, PROJECT_NAME } from "commons/constants/env";
import { useTranslation } from "react-i18next";
import { format, isValid, parse } from "date-fns";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSearchParams } from "react-router-dom";
import { isObject } from "lodash";

const DashboardStats = () => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();

    const today = format(new Date(), "yyyy-MM-dd");
    const [researchStartedAt, setResearchStartedAt] = useState<Date | any>();

    /** Loading data state status */
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [isSurveyLoading, setIsSurveyLoading] = useState(false);

    /** This state is holding stats about research */
    const stats = useAppSelector(selectStats);
    const statsSurvey = useAppSelector(selectStatsSurveys);

    const isSurveyResourceLoading = useAppSelector(selectSurveyResourceLoading);

    const sortingOptions = {
      'Od najstarszego': { "order[createdAt]": 'ASC' },
      'Od najnowszego': { "order[createdAt]": 'DESC' }
    };
    const [filters, setFilters] = useState<any>(Object.fromEntries(searchParams));

    /** Fetch data  */
    const fetchData = async () => {
      setIsDataLoading(true);
      const stats = await dispatch(fetchStats()).unwrap();
      setIsDataLoading(false);
    };

    /** Fetch data survey  */
    const fetchDataSurvey = async () => {
      setIsSurveyLoading(true);

      if (stats?.researchStartedAt) {
        const parsedDate = parse(stats.researchStartedAt, "yyyy-MM-dd", new Date());
        const formattedDate = format(parsedDate, "dd.MM.yyyy");
        setResearchStartedAt(formattedDate);
      }

      const config = {
        headers: {
          'Content-Type': 'application/hal+json',
          Accept: 'application/hal+json',
        },
        params: {
          // ...Object.fromEntries(searchParams)
          // ...Object.fromEntries(filters)
          ...filters
        },
      }

      await dispatch(fetchStatsSurveys(config));
      setIsSurveyLoading(false);
    };


    const handleChange = async (value: any, type: any) => {
      if (isValid(value)) {
        filters[type] = format(value, DATE_FORMAT);
      } if (isObject(value) || type === 'order') {
        Object.entries(JSON.parse(value)).forEach(
          ([label, val]) => filters[label] = val
        );
      } else {
        filters[type] = value;
      }

      setFilters((previousState: any) => ({
        ...previousState,
        ...filters,
      }));
    };

    useEffect(() => {
        fetchData();
        fetchDataSurvey();
    }, [dispatch]);

    useEffect(() => {
        fetchDataSurvey();
    }, [filters]);

    return (
        <>
            {isDataLoading ? (
                <Box p={1} mt={5} maxWidth="100%" ml="auto" mr="auto" display="grid" style={{placeItems: "center"}}>
                    <CircularProgress/>
                </Box>
            ) : (
                <Box p={1} mt={5} maxWidth="100%" ml="auto" mr="auto" style={{ letterSpacing: "0.3px" }}>
                    <StyledHeaderContainer>
                        <StyledHeaderTitle>{PROJECT_NAME}</StyledHeaderTitle>
                        <StyledHeaderSubtitle>{t('statistics')}</StyledHeaderSubtitle>
                        <StyledHeaderDate>{researchStartedAt} - {t('now')}</StyledHeaderDate>
                    </StyledHeaderContainer>

                    <StyledGlobalContainer>
                        <Box>
                            <StyledStatsTitle>
                                <StyledStatsDotPurple/>
                                <StyledStatsTitleText>{stats?.item[0]?.name}</StyledStatsTitleText>
                            </StyledStatsTitle>

                            <StyledGlobalStatsContainer>
                                {stats?.item[0]?.item?.map((element: FluffyItem) => (
                                    <StyledStatsTab key={element.name}>
                                        <StyledSText1>{element.count}</StyledSText1>
                                        <StyledSText2>{element.name}</StyledSText2>
                                    </StyledStatsTab>
                                ))}
                            </StyledGlobalStatsContainer>
                        </Box>

                        <Box>
                            <StyledStatsTitle>
                                <StyledStatsDotPurple/>
                                <StyledStatsTitleText>{stats?.item[1]?.name}</StyledStatsTitleText>
                            </StyledStatsTitle>

                            <StyledGlobalStatsContainer>
                                {stats?.item[1]?.item?.map((element: FluffyItem) => (
                                    <StyledStatsTab key={element.name}>
                                        <StyledSText1>{element.count}</StyledSText1>
                                        <StyledSText2>{element.name}</StyledSText2>
                                    </StyledStatsTab>
                                ))}
                            </StyledGlobalStatsContainer>
                        </Box>
                    </StyledGlobalContainer>

                    <StyledGlobalAccordionContainer>
                      <Accordion defaultExpanded style={{ boxShadow: "none"}}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel3-content"
                          id="panel3-header"
                          style={{ padding: 0 }}
                        >
                          <StyledStatsTitle>
                            <StyledStatsDotYellow/>
                            <StyledStatsTitleText>{stats?.item[2]?.name}</StyledStatsTitleText>
                          </StyledStatsTitle>
                        </AccordionSummary>
                        <AccordionDetails>
                          <StyledGlobalAccordionFiltersContainer>
                            <Box display="flex" alignItems="center" style={{gap: "20px"}}>
                              <StyledGlobalAccordionFiltersInput
                                type="date"
                                defaultValue={stats?.researchStartedAt}
                                disabled={true}
                              />
                              -
                              <StyledGlobalAccordionFiltersInput
                                type="date"
                                defaultValue={today}
                                disabled={true}
                              />
                            </Box>

                            <Box display="flex" style={{gap: "10px"}}>
                              <StyledGlobalAccordionFiltersInput
                                type="text"
                                placeholder={`${t('search')}...`}
                              />
                              <StyledGlobalAccordionFiltersButton>
                                {t('export')}
                              </StyledGlobalAccordionFiltersButton>
                              <StyledGlobalAccordionFiltersButton>
                                {t('sort-by')}
                              </StyledGlobalAccordionFiltersButton>
                            </Box>
                          </StyledGlobalAccordionFiltersContainer>

                          {stats?.item[2]?.item?.map((element: PurpleItem) => (
                            <MedicalCenterItem
                              key={element.name}
                              number={element.number}
                              name={element.name}
                              item={element.item}
                            />
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    </StyledGlobalAccordionContainer>

                    <StyledGlobalAccordionContainer>
                      <Accordion defaultExpanded style={{ boxShadow: "none"}}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel3-content"
                          id="panel3-header"
                          style={{ padding: 0 }}
                        >
                          <StyledStatsTitle>
                            <StyledStatsDotGreen/>
                            <StyledStatsTitleText>Information about all survey's</StyledStatsTitleText>
                          </StyledStatsTitle>
                        </AccordionSummary>
                        <AccordionDetails>
                          <StyledGlobalAccordionFiltersContainer>
                            <Box display="flex" alignItems="center" style={{ gap: "20px" }}>
                              <StyledGlobalAccordionFiltersInput
                                name={'createdAt[after]'}
                                type="date"
                                defaultValue={stats?.researchStartedAt}
                                disabled={isSurveyLoading}
                                onBlur={(e: any) => handleChange(e.target.value, 'createdAt[after]')}
                                onKeyPress={(e: any) => e.preventDefault()}
                              />
                              -
                              <StyledGlobalAccordionFiltersInput
                                name={'createdAt[before]'}
                                type="date"
                                defaultValue={today}
                                disabled={isSurveyLoading}
                                onBlur={(e: any) => handleChange(e.target.value, 'createdAt[before]')}
                                onKeyPress={(e: any) => e.preventDefault()}
                              />
                            </Box>

                            <Box display="flex" style={{ gap: "10px" }}>
                              {/*<StyledGlobalAccordionFiltersInput*/}
                              {/*  type="text"*/}
                              {/*  placeholder={`${t('search')}...`}*/}
                              {/*  onChange={(newValue: any) => handleChange(newValue, 'randomId')}*/}
                              {/*  disabled={true}*/}
                              {/*/>*/}
                              <Button
                                color='secondary'
                                variant='outlined'
                                size='small'
                                fullWidth
                                onClick={() => dispatch(exportFile())}
                                style={{ whiteSpace: 'nowrap', fontWeight: 'bold', fontSize: '12px' }}
                                disabled={isSurveyResourceLoading}
                                startIcon={
                                  isSurveyResourceLoading ? <CircularProgress size={12} /> : undefined
                                }
                              >
                                {t('export')}
                              </Button>
                              <TextField
                                name="order"
                                label={t('sort-by')}
                                select
                                fullWidth
                                size='small'
                                variant='outlined'
                                onChange={(e) => handleChange(e.target.value, 'order')}
                                disabled={isSurveyLoading}
                                style={{ minWidth: "170px" }}
                                sx={{ "& .MuiInputBase-input": { height: "20px", padding: "10px"} }}
                              >
                                {Object.entries(sortingOptions).map(([key, value]) => (
                                  <MenuItem key={key} value={JSON.stringify(value)}>
                                    {t(key)}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Box>
                          </StyledGlobalAccordionFiltersContainer>

                          {isSurveyLoading ? (
                            <Box p={1} mt={5} maxWidth="100%" ml="auto" mr="auto" display="grid" style={{placeItems: "center"}}>
                              <CircularProgress/>
                            </Box>
                          ) :
                            statsSurvey._embedded?.item?.map((element: PurpleItem) => (
                              <SurveyItem
                                key={element.name}
                                number={element.number}
                                name={element.name}
                                item={element.item}
                              />
                            ))
                        }
                        </AccordionDetails>
                      </Accordion>
                    </StyledGlobalAccordionContainer>
                </Box>
            )}
        </>
    );
};

export default DashboardStats;
